import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Upload, FileText, X, Loader2, ArrowDownCircle, RefreshCw } from 'lucide-react';
import DocumentCard from '../components/documents/DocumentCard';
import { useDocuments } from '../hooks/useDocuments';
import { ROUTES } from '../lib/constants';

const DocumentCardSkeleton = () => (
  <div className="bg-white/60 border border-cream-300 rounded-xl p-5 space-y-4 animate-pulse shadow-sm">
    <div className="flex justify-between items-start">
      <div className="p-3 bg-brown-100 rounded-lg w-12 h-12" />
      <div className="h-6 w-20 bg-brown-100 rounded-full" />
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-brown-200 rounded w-3/4" />
      <div className="h-3 bg-brown-100 rounded w-1/2" />
    </div>
    <div className="h-9 bg-brown-100 rounded-lg w-full mt-4" />
  </div>
);

const DocumentHistory = () => {
  const navigate  = useNavigate();
  const [inputValue,   setInputValue]   = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debounce search — 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(inputValue), 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const {
    documents,
    loading,
    loadingMore,
    hasMore,
    loadMoreDocuments,
    deleteDocument,
    refreshDocuments,
  } = useDocuments({ search: searchQuery, status: statusFilter });

  // Manual refresh handler — stable, no dependency array issues
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    refreshDocuments();
    // Give it a moment to feel responsive then clear spinner
    setTimeout(() => setIsRefreshing(false), 800);
  }, [refreshDocuments]);

  // Check if any document is still processing — shows hint to user
  const hasProcessing = documents.some(
    (d) => d.processing_status === 'processing' || d.status === 'processing'
  );

  // Chronological grouping
  const groupedDocuments = useMemo(() => {
    const groups = { today: [], yesterday: [], thisWeek: [], thisMonth: [], older: {} };
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    const yesterdayDate = new Date(today);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = yesterdayDate.getTime();

    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoTime = weekAgo.getTime();

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    documents.forEach(doc => {
      if (!doc.created_at) return;
      const docDate = new Date(doc.created_at);
      const docTime = docDate.getTime();
      const docDay  = new Date(
        docDate.getFullYear(), docDate.getMonth(), docDate.getDate()
      ).getTime();

      if (docDay === today)              groups.today.push(doc);
      else if (docDay === yesterday)     groups.yesterday.push(doc);
      else if (docTime >= weekAgoTime)   groups.thisWeek.push(doc);
      else if (docTime >= monthStart)    groups.thisMonth.push(doc);
      else {
        const monthYear = docDate.toLocaleDateString('en-US', {
          month: 'long', year: 'numeric'
        });
        if (!groups.older[monthYear]) groups.older[monthYear] = [];
        groups.older[monthYear].push(doc);
      }
    });

    return groups;
  }, [documents]);

  const handleDelete = async (id, url) => {
    await deleteDocument(id, url);
  };

  const clearFilters = () => {
    setInputValue('');
    setSearchQuery('');
    setStatusFilter('all');
  };

  const isFiltering = inputValue.trim() !== '' || statusFilter !== 'all';

  return (
    <div className="w-full animate-fade-in">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-1 sm:mb-2">
              Document History
            </h1>
            <p className="text-sm sm:text-base text-brown-600 h-5">
              {loading ? (
                <span className="inline-block w-40 h-4 bg-brown-200/60 rounded animate-pulse" />
              ) : (
                `Showing ${documents.length} loaded records`
              )}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            {/* Refresh button — shown always, pulses when processing */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              title="Refresh history"
              className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border-2 font-medium text-sm transition-all disabled:opacity-50 ${
                hasProcessing
                  ? 'border-brown-400 text-brown-700 bg-brown-50 hover:bg-brown-100'
                  : 'border-cream-300 text-brown-600 bg-white/60 hover:bg-cream-50'
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              <span className="hidden sm:inline">
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </span>
            </button>

            <button
              onClick={() => navigate(ROUTES.UPLOAD)}
              className="flex items-center justify-center space-x-2 px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 text-white rounded-lg sm:rounded-xl transition-all shadow-md font-medium text-sm sm:text-base"
            >
              <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Upload New</span>
            </button>
          </div>
        </div>

        {/* Processing hint banner */}
        {hasProcessing && !loading && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm text-blue-700 font-medium">
            <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            <span>Some documents are still processing. Tap Refresh to check for updates.</span>
          </div>
        )}

        {/* Search + Filter Bar */}
        <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border border-cream-300/50 p-3 sm:p-4 md:p-5">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  disabled={loading}
                  placeholder={loading ? "Warming up archive panel..." : "Search filename or summary content..."}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-2.5 md:py-3 bg-white/60 border-2 border-cream-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brown-500 focus:border-brown-500 outline-none transition-all text-sm sm:text-base placeholder:text-brown-400 disabled:opacity-75"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative">
                <Filter className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
                <select
                  value={statusFilter}
                  disabled={loading}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full sm:w-auto pl-10 sm:pl-12 pr-8 sm:pr-10 py-2 sm:py-2.5 md:py-3 bg-white/60 border-2 border-cream-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brown-500 focus:border-brown-500 outline-none transition-all text-sm sm:text-base appearance-none cursor-pointer disabled:opacity-75"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {isFiltering && (
                <button
                  onClick={clearFilters}
                  className="flex items-center justify-center space-x-1.5 px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-brown-600 hover:text-brown-900 hover:bg-cream-50/50 rounded-lg transition-all text-sm sm:text-base font-medium"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document List */}
      {loading ? (
        <div>
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">
            Retrieving Records...
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            <DocumentCardSkeleton />
            <DocumentCardSkeleton />
            <DocumentCardSkeleton />
          </div>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 sm:py-16 md:py-20">
          <FileText className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-cream-300 mx-auto mb-4 sm:mb-6" />
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-brown-900 mb-2 sm:mb-3">
            {isFiltering ? 'No matching documents found' : 'No documents yet'}
          </h3>
          <p className="text-sm sm:text-base md:text-lg text-brown-600 mb-6 sm:mb-8 px-4">
            {isFiltering
              ? 'Double check your spelling or adjust status filter selection parameters'
              : 'Upload your first textbook page to get started'}
          </p>
          <button
            onClick={isFiltering ? clearFilters : () => navigate(ROUTES.UPLOAD)}
            className="px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 text-white rounded-lg transition-all shadow-md font-medium text-sm sm:text-base"
          >
            {isFiltering ? 'Reset Filters' : 'Upload Document'}
          </button>
        </div>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {groupedDocuments.today.length > 0 && (
            <div>
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">Today</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {groupedDocuments.today.map(doc => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {groupedDocuments.yesterday.length > 0 && (
            <div>
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">Yesterday</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {groupedDocuments.yesterday.map(doc => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {groupedDocuments.thisWeek.length > 0 && (
            <div>
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">This Week</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {groupedDocuments.thisWeek.map(doc => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {groupedDocuments.thisMonth.length > 0 && (
            <div>
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">This Month</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {groupedDocuments.thisMonth.map(doc => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {Object.entries(groupedDocuments.older).map(([monthYear, docs]) => (
            <div key={monthYear}>
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-3 sm:mb-4 px-1">
                {monthYear}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {docs.map(doc => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="mt-12 pt-4 flex justify-center">
              <button
                onClick={loadMoreDocuments}
                disabled={loadingMore}
                className="flex items-center space-x-2 px-6 py-3 bg-white border-2 border-cream-300 hover:border-brown-400 text-brown-700 hover:text-brown-900 font-semibold text-sm sm:text-base rounded-xl transition-all shadow-sm disabled:opacity-60"
              >
                {loadingMore ? (
                  <Loader2 className="w-5 h-5 animate-spin text-brown-600" />
                ) : (
                  <ArrowDownCircle className="w-5 h-5 text-brown-600" />
                )}
                <span>{loadingMore ? 'Searching remaining records...' : 'Load More History'}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentHistory;